import { initialize, LDClient } from 'launchdarkly-js-client-sdk';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import React from 'react';

import { VideoProvider } from 'src/shared/models/shared';

const getClientSideID = () => process.env.REACT_APP_LAUNCH_DARKLY_CLIENT_ID || '';

export interface FeatureFlags {
    showTopicsOfFocusProfileWeb: boolean;
    adminDashboardShowUpNextModal: boolean;
    showHrisConnectButton: boolean;
    enableAdminPortal: boolean;
    showPersonalizedAssessment: boolean;
    showNewEmploymentQuestions: boolean;
    hidePhq9QuestionsWeb: boolean;
    hidePhq8Gad7Assessments: boolean;
    who5ScoreThresholdForTherapy: number;
    wellbeingOnlyWho5: boolean;
    showFullAssessmentMember: boolean;
    hideTriageSymptoms: boolean;
    hideTosPrivacyUpdateMemberWeb: boolean;
    showKnTerms: boolean;
    showEuPersonalHealthInfoTerm: boolean;
    migrateToBranchio: boolean;
    workEmailAddressChangeRequired: boolean;
    showSupportRequestCarenav: boolean;
    inviteDependents: boolean;
    showCoursesOnWeb: boolean;
    enableInternationalizedCoursesWeb: boolean;
    showBackendPrograms: boolean;
    hpiHomescreenPendingSessions: boolean;
    showCirclesInMemberWeb: boolean;
    showDailyPauseOnWeb: boolean;
    minimumDependentAge: number;
    hpiMemberEligibilityFailures: boolean;
    showProgramsEarlyExitFeedback: boolean;
    showRateProgramPageWeb: boolean;
    hideSessionSchedulingWeb: boolean;
    showAlreadyWorkingWithTherapist: boolean;
    showDemographicsProjectWeb: boolean;
    showDemographicsMhcWeb: boolean;
    showDemographicsTherapyWeb: boolean;
    showChineseDisclaimer: boolean;
    releaseDiginShowRecommendedPageWeb: boolean;
    showNewExplorePageWeb: boolean;
    releaseDiginShowDailyPauseCalendarRemindersWeb: boolean;
    releaseDiginShowDailyPauseNotificationsWeb: boolean;
    releaseDiginShowDailyPauseStreaksWeb: boolean;
    startDailyPauseStreaksExperimentWeb: boolean;
    hpiMemberAnytimeEligibilityStatus: boolean;
    showAlternateFeedbackCopy: boolean;
    showIntakeForm: boolean;
    videoProvider: VideoProvider;
    hideNpsInSessionRating: boolean;
    disableMemberScheduleForPammProviders: boolean;
    showCrisisResourceAhpCopy: boolean;
    showSuicideAndCrisisLifeline: boolean;
    showTimelyResponseBanner: boolean;
    showAdminTimelyResponseBanner: string;
    showTimeToCare: boolean;
    alternativeEmailFlow: boolean;
    hideRequestPersonalEmailFlowWeb: boolean;
    translationUi: boolean;
    showTherapyForAll: boolean;
    dailyPauseStreaksExperimentVariant: 'treatment' | 'control' | 'ineligible';
    allowSelectableLanguagePreference: boolean;
    releaseDiginShowDailyPauseCalendarRemindersMobile: boolean;
    releaseDiginShowDailyPauseNotificationsMobile: boolean;
    showAdminDropdownMenu: boolean;
    climbSsoOnly: boolean;
    circlesShowOnDemand: boolean;
    circlesShowOnDemandFeedback: boolean;
    hpiAutomaticPaymentsEnabled: boolean;
    timeZoneModal: boolean;
    fdrEnableClickwrapConsentsWebRelease: boolean;
    circlesShowInternationalCircles: boolean;
    pammCostEstimation: boolean;
    explorePageConnectWithSupport: boolean;
    showRegistrationSurveyWeb: boolean;
    adminPortalPermissionsV1Enabled: boolean;
    clientPortalUnification: boolean;
}

declare module 'launchdarkly-react-client-sdk' {
    export function useFlags(): FeatureFlags;
}

// This is the default state when LaunchDarkly has not fetched the flags yet.
// The values should be equal to what is given in LaunchDarkly in "if targeting is off, serve {checkboxValue}"
export const defaultFeatureFlags: FeatureFlags = {
    showTopicsOfFocusProfileWeb: false,
    adminDashboardShowUpNextModal: false,
    showHrisConnectButton: false,
    enableAdminPortal: false,
    showPersonalizedAssessment: false,
    showNewEmploymentQuestions: false,
    hidePhq9QuestionsWeb: false,
    hidePhq8Gad7Assessments: false,
    who5ScoreThresholdForTherapy: 28,
    wellbeingOnlyWho5: false,
    showFullAssessmentMember: false,
    hideTriageSymptoms: false,
    hideTosPrivacyUpdateMemberWeb: false,
    showKnTerms: false,
    showEuPersonalHealthInfoTerm: false,
    migrateToBranchio: false,
    workEmailAddressChangeRequired: false,
    showSupportRequestCarenav: false,
    inviteDependents: false,
    showCoursesOnWeb: false,
    enableInternationalizedCoursesWeb: false,
    showBackendPrograms: false,
    hpiHomescreenPendingSessions: false,
    showCirclesInMemberWeb: false,
    showDailyPauseOnWeb: false,
    minimumDependentAge: 13,
    hpiMemberEligibilityFailures: false,
    showProgramsEarlyExitFeedback: false,
    showRateProgramPageWeb: false,
    hideSessionSchedulingWeb: false,
    showAlreadyWorkingWithTherapist: false,
    showDemographicsProjectWeb: false,
    showDemographicsMhcWeb: false,
    showDemographicsTherapyWeb: false,
    showChineseDisclaimer: false,
    releaseDiginShowRecommendedPageWeb: false,
    showNewExplorePageWeb: false,
    releaseDiginShowDailyPauseCalendarRemindersWeb: false,
    releaseDiginShowDailyPauseNotificationsWeb: false,
    releaseDiginShowDailyPauseStreaksWeb: false,
    startDailyPauseStreaksExperimentWeb: false,
    hpiMemberAnytimeEligibilityStatus: false,
    showAlternateFeedbackCopy: false,
    showIntakeForm: false,
    videoProvider: VideoProvider.ZoomLink,
    hideNpsInSessionRating: false,
    disableMemberScheduleForPammProviders: false,
    showCrisisResourceAhpCopy: false,
    showSuicideAndCrisisLifeline: false,
    showTimelyResponseBanner: false,
    showAdminTimelyResponseBanner: '',
    showTimeToCare: false,
    alternativeEmailFlow: false,
    hideRequestPersonalEmailFlowWeb: false,
    translationUi: false,
    showTherapyForAll: false,
    dailyPauseStreaksExperimentVariant: 'ineligible',
    allowSelectableLanguagePreference: true,
    releaseDiginShowDailyPauseCalendarRemindersMobile: false,
    releaseDiginShowDailyPauseNotificationsMobile: false,
    showAdminDropdownMenu: false,
    climbSsoOnly: false,
    circlesShowOnDemand: false,
    circlesShowOnDemandFeedback: false,
    hpiAutomaticPaymentsEnabled: false,
    timeZoneModal: false,
    fdrEnableClickwrapConsentsWebRelease: false,
    circlesShowInternationalCircles: false,
    pammCostEstimation: false,
    explorePageConnectWithSupport: true,
    showRegistrationSurveyWeb: false,
    adminPortalPermissionsV1Enabled: false,
    clientPortalUnification: false,
};

export const ldClient = initialize(
    getClientSideID(),
    { anonymous: true },
    {
        allAttributesPrivate: true,
        sendEvents: true,
    },
);

export function getFlag<Value extends string | boolean | number = boolean>(
    flag: string,
    defaultValue?: Value,
): Value {
    return ldClient.variation(flag, defaultValue);
}

export async function getLDClient(): Promise<LDClient> {
    await ldClient.waitUntilReady();

    return ldClient;
}

export const LaunchDarklyProvider: React.FC<unknown> = props => {
    return (
        <LDProvider
            clientSideID={getClientSideID()}
            ldClient={getLDClient()}
            options={{
                bootstrap: defaultFeatureFlags,
            }}
        >
            {props.children}
        </LDProvider>
    );
};
